import type { AssociablePackageInfo, PackageInfo } from "./content";
import type { Env } from "./env";
import type { Language, TranslationKey } from "./localization";
import type { GlobalLocationState } from "./location";
import type { FeatureFlags, ToolSetting } from "./settings";

export enum ToolKey {
  Abus = "abus",
  BikeFrameSizeCalculator = "bike-frame-size-calculator",
  BikeFinancialCalculator = "bike-financial-calculator",
  BikepriceCalculator = "bikeprice-calculator",
  LeasingCheck = "leasing-check",
  AllAboutCompanyBike = "all-about-company-bike",
  Upway = "upway"
}

export enum ExternalToolKey {
  Alteos = "alteos",
  Bikmo = "bikmo",
  BcRemoteShop = "bcRemoteShop",
  PaulLange = "paulLange",
  BicoB2bShop = "bicoShop",
  BodyScanningCrm = "bodyScanningCrm",
  BodyScanningAppointmentPrep = "bodyScanningAppointmentPrep",
  Enra = "enra",
  JobRad = "jobRad",
  WuerthBikeLease = "wuerthBikeLease",
  BicoAssessment = "bicoAssessment",
  Cycleasy = "cycleasy",
  SqlabProfiler = "sqlabProfiler",
  Wertgarantie = "wertgarantie",
  LinexoLeasing = "linexoLeasing",
  // eslint-disable-next-line @typescript-eslint/naming-convention -- eslint does not recognize "LeaseABike" as PascalCase
  LeaseABike = "leaseABike",
  BusinessBike = "businessbike",
  EasyBikeLeasing = "easyBikeLeasing",
  BikeLeasing = "bikeleasing",
  LeaseMyBike = "leaseMyBike",
  DeutscheDienstrad = "deutscheDienstrad",
  FirmenRadl = "firmenRadl",
  EuroRad = "euroRad",
  KazenMaier = "kazenMaier",
  MeinDienstrad = "meinDienstrad",
  Willdienstrad = "willdienstrad",
  ZegPlusGarantie = "zegPlusGarantie",
  KtmConfigurator = "ktmConfigurator",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  KtmBookATestride = "ktmBookATestride",
  KtmParts = "ktmParts",
  KtmWear = "ktmWear",
  HusqvarnaConfigurator = "husqvarnaConfigurator",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  HusqvarnaBookATestride = "husqvarnaBookATestride",
  HusqvarnaParts = "husqvarnaParts",
  HusqvarnaWear = "husqvarnaWear",
  GasGasConfigurator = "gasgasConfigurator",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  GasgasBookATestride = "gasgasBookATestride",
  GasGasParts = "gasgasParts",
  GasGasWear = "gasgasWear",
  Smartfit = "smartfit",
  BoschPartsAdviser = "boschPartsAdviser",
  SchwalbePartsAdviser = "schwalbePartsAdviser",
  HephaErgoAdjust = "hephaErgoAdjust"
}

export type ToolLabel = Partial<Record<TranslationKey, string>> & {
  [Language.De]: string;
};

export enum ToolCategory {
  Advice = "advice",
  Service = "service",
  Leasing = "leasing",
  Configurator = "configurator",
  Testride = "testride",
  Integration = "integration",
  PartsAndWear = "partsAndWear"
}

interface ToolBase extends AssociablePackageInfo {
  label: ToolLabel;
  category: ToolCategory;
}

export interface ToolPackageInfo extends PackageInfo, ToolBase {
  /**
   * The entry file specified in the tool package meta.json (allows package-level override of the default index.html).
   */
  entryFile?: string | Partial<Record<TranslationKey, string>>;

  /**
   * Tools will be displayed in order of their priority.
   * The tool with the smallest number will be displayed first, and so on.
   */
  priority: number;

  /**
   * Internal icon id which is only used for internal tools and is defined in the meta.json of each internal tool.
   * Furthermore it is used for internal icon mapping. e.g. icon "frame" (first character uppercase) is the Suffix of IconLargeFrame.
   */
  icon?: string;

  /**
   * Alternative to `icon` property, this allows to specify a custom icon file path within the package.
   */
  iconFilePath?: string;
}

// Tools and TestUrls can be localized using environment.locale in the Tool/TestUrlFunction
export type ToolUrlFunction<
  CustomToolSettings extends ToolSetting = ToolSetting,
  LocationState extends GlobalLocationState = GlobalLocationState
> = (
  environment: Env,
  customToolSettings?: CustomToolSettings,
  externalToolLocationState?: LocationState
) => string | null;

export type TestUrlFunction<CustomToolSettings extends ToolSetting = ToolSetting> = (
  environment: Env,
  customToolSettings?: CustomToolSettings
) => string | null;

export type IconFunction = (environment: Env) => string;

export interface ExternalToolBase extends ToolBase {
  key: ExternalToolKey;
  initialViewSettingsRequired?: boolean;
  route?: string;
  featureFlag?: keyof FeatureFlags;
}

export interface ExternalIntegrationToolConfig extends ExternalToolBase {
  category: ToolCategory.Integration;
}

export type ExternalWebviewToolConfig<
  CustomToolSettings extends ToolSetting = ToolSetting,
  LocationState extends GlobalLocationState = GlobalLocationState
> = ExternalToolBase & {
  category: Omit<ToolCategory, ToolCategory.Integration>;
  icon: string | IconFunction; // Assigns external icon or returns external icon depending on IconFunction
  persistWebviewSession: boolean; // Persists cookies and storage even if the device gets restarted
  clearAllSessionDataOnClose: boolean; // Clears cookies and storage after each tool close
  disableAutomaticKeyboard?: boolean; // Prevents the keyboard from opening when inputs are focused
  toolUrl: string | ToolUrlFunction<CustomToolSettings, LocationState>;
  testUrl: string | TestUrlFunction<CustomToolSettings>;
  zoomFactor?: number;
  width?: number;
  pinRequired: boolean;
  metadata: string[];
  bgColor?: string;
  printable: boolean;
  registerUrl?: string;
  exitButtonAsFloatingButton?: boolean;
};

export type ExternalToolConfig = ExternalIntegrationToolConfig | ExternalWebviewToolConfig;

export interface ExternalToolStatus {
  [toolKey: string]: boolean;
}
